import { useEffect, useState, type ReactNode } from 'react'
import { restzeit } from '@k4ia/shared'
import { Avatar } from '../components/Avatar'
import {
  fetchGewinnspielPreise,
  fetchGewinnspielState,
  sendGewinnspielBefehl,
  startGewinnspiel,
  type GewinnspielPreis,
  type GewinnspielStand,
} from '../lib/api'

/** Wie oft das Handy nachsieht, was der Bildschirm zeigt. */
const POLL_MS = 1000

/**
 * `/gewinnspiel/go/<token>`: das Handy, das den Gewinnspiel-Bildschirm steuert.
 *
 * „Ziehung starten" zieht für jeden Preis einen Namen. Danach steht hier je
 * Preis der Gezogene mit laufender Frist: „Gewinner ist da" hakt ab, „Neu
 * ziehen" ersetzt ihn sofort (wer nicht da ist, kommt nicht mehr dran).
 * Läuft die Frist ab, zieht der Worker von selbst neu. Zurücksetzen bringt
 * alles auf Anfang — mit Rückfrage.
 */
export function GewinnspielRemotePage({ token }: { token: string }) {
  const [stand, setStand] = useState<GewinnspielStand | null>(null)
  const [preise, setPreise] = useState<GewinnspielPreis[]>([])
  const [fehler, setFehler] = useState<string | null>(null)
  const [busy, setBusy] = useState<string | null>(null)
  const [jetzt, setJetzt] = useState(() => Date.now())

  const ladePreise = () =>
    fetchGewinnspielPreise()
      .then((r) => setPreise(r.preise))
      .catch(() => undefined)

  useEffect(() => {
    void ladePreise()
  }, [])

  useEffect(() => {
    let aktiv = true
    const takt = async () => {
      try {
        const neu = await fetchGewinnspielState()
        if (aktiv) setStand(neu)
      } catch {
        // Netz kurz weg: beim nächsten Durchlauf wieder
      }
    }
    void takt()
    const id = setInterval(() => {
      void takt()
      setJetzt(Date.now())
    }, POLL_MS)
    return () => {
      aktiv = false
      clearInterval(id)
    }
  }, [])

  async function tun(was: string, arbeit: () => Promise<GewinnspielStand>) {
    setFehler(null)
    setBusy(was)
    try {
      setStand(await arbeit())
    } catch (e) {
      setFehler(e instanceof Error ? e.message : 'Das hat nicht geklappt.')
    } finally {
      setBusy(null)
    }
  }

  const lose = preise.reduce((n, p) => n + p.winnersCount, 0)

  function starten() {
    if (
      !window.confirm(
        `Jetzt ziehen? K4ia zieht ${lose} Gewinner für ${preise.length} Preise, jeden aus der Liste seines Preises.`,
      )
    )
      return
    void tun('start', () => startGewinnspiel(token))
  }

  function zuruecksetzen() {
    if (!window.confirm('Ziehung zurücksetzen? Alle gezogenen Namen und Häkchen gehen verloren.'))
      return
    void tun('reset', () => sendGewinnspielBefehl(token, { art: 'reset' }))
  }

  if (!stand) {
    return (
      <Rahmen avatar="thinking">
        <p className="text-xl">Einen Moment …</p>
      </Rahmen>
    )
  }

  if (!stand.verbunden) {
    return (
      <Rahmen avatar="idle">
        <p className="text-xl">Der Bildschirm ist nicht verbunden.</p>
        <p className="text-text-muted">
          Öffne dort <code className="font-mono">/gewinnspiel</code> und klicke einmal hinein.
        </p>
      </Rahmen>
    )
  }

  const fehlerZeile = fehler && (
    <p className="text-center text-sm text-status-error" role="alert">
      {fehler}
    </p>
  )

  if (stand.phase === 'wartet') {
    const ohneListe = preise.filter((p) => p.participantCount === 0)
    const bereit = preise.length > 0 && ohneListe.length === 0
    return (
      <Rahmen avatar={fehler ? 'error' : 'idle'}>
        <p className="text-xl">Gewinnspiel</p>
        <ul className="w-full text-left text-sm text-text-muted" data-testid="handy-uebersicht">
          {preise.map((p) => (
            <li key={p.id} className="flex justify-between gap-3 py-0.5">
              <span className="truncate">
                {p.company}
                {p.winnersCount > 1 ? ` (${p.winnersCount} Gewinner)` : ''}
              </span>
              <span className="shrink-0 tabular-nums">{p.participantCount} Teilnehmer</span>
            </li>
          ))}
        </ul>
        {preise.length === 0 && (
          <p className="text-sm text-text-muted">
            Im Admin unter „Gewinnspiel“ fehlen noch Preise.
          </p>
        )}
        {ohneListe.length > 0 && (
          <p className="text-sm text-status-error">
            Ohne Teilnehmerliste: {ohneListe.map((p) => p.company).join(', ')} — im Admin hochladen.
          </p>
        )}
        <button
          onClick={starten}
          disabled={busy !== null || !bereit}
          className="btn-primary w-full rounded-2xl px-8 py-5 text-xl font-semibold disabled:opacity-50"
        >
          {busy === 'start' ? 'Zieht …' : 'Ziehung starten'}
        </button>
        {fehlerZeile}
      </Rahmen>
    )
  }

  const fertig = stand.phase === 'fertig'
  const offen = stand.preise.filter((p) => p.bestaetigtAm === null).length
  return (
    <main className="mx-auto flex min-h-[calc(100dvh_-_var(--buehne-leiste,0px))] max-w-md flex-col gap-4 bg-bg p-5">
      <header className="flex items-center gap-3">
        <Avatar state={fertig ? 'idle' : 'speaking'} size={56} showLabel={false} />
        <div>
          <p className="text-lg font-semibold">
            {fertig ? 'Alle Gewinner stehen fest' : 'Ziehung läuft'}
          </p>
          <p className="text-sm text-text-muted">
            {fertig
              ? 'Herzlichen Glückwunsch!'
              : `${offen} ${offen === 1 ? 'Gewinner meldet' : 'Gewinner melden'} sich noch`}
          </p>
        </div>
      </header>
      {fehlerZeile}

      <ul className="flex flex-col gap-3">
        {stand.preise.map((z) => {
          const preis = preise.find((p) => p.id === z.preisId)
          const da = z.bestaetigtAm !== null
          const niemand = z.gewinner === null
          const titel =
            preis && preis.winnersCount > 1
              ? `${preis.company} · Gewinner ${z.los + 1} von ${preis.winnersCount}`
              : (preis?.company ?? z.preisId)
          return (
            <li
              key={z.id}
              data-testid="handy-preis"
              className={`rounded-2xl border-2 p-4 ${
                da
                  ? 'border-[var(--color-status-active)] bg-bg-elevated'
                  : 'border-border bg-bg-elevated'
              }`}
            >
              <p className="text-sm text-text-muted">{titel}</p>
              <div className="mt-1 flex items-baseline justify-between gap-3">
                <p className="break-all font-mono text-xl font-bold">
                  {niemand ? 'Niemand mehr übrig' : `@${z.gewinner}`}
                </p>
                {!da && !niemand && (
                  <p className="shrink-0 font-mono text-lg tabular-nums text-text-muted">
                    {restzeit(z.fristBis, jetzt)}
                  </p>
                )}
              </div>
              {da ? (
                <p className="mt-2 inline-flex items-center gap-1 text-sm font-semibold text-status-active">
                  <span aria-hidden>✓</span> Gewinner ist da
                </p>
              ) : niemand ? null : (
                <div className="mt-3 flex gap-2">
                  <button
                    onClick={() =>
                      void tun(z.id, () =>
                        sendGewinnspielBefehl(token, { art: 'bestaetigen', losId: z.id }),
                      )
                    }
                    disabled={busy !== null}
                    className="btn-primary flex-1 rounded-xl px-4 py-3 font-semibold disabled:opacity-50"
                  >
                    <span aria-hidden>✓ </span>Gewinner ist da
                  </button>
                  <button
                    onClick={() => {
                      if (window.confirm(`@${z.gewinner} ist nicht da? Dann zieht K4ia jetzt neu.`))
                        void tun(z.id, () =>
                          sendGewinnspielBefehl(token, { art: 'neu', losId: z.id }),
                        )
                    }}
                    disabled={busy !== null}
                    className="rounded-xl border border-border px-4 py-3 font-semibold disabled:opacity-50"
                  >
                    Neu ziehen
                  </button>
                </div>
              )}
            </li>
          )
        })}
      </ul>

      {stand.nichtDa.length > 0 && (
        <p className="text-xs text-text-muted">
          Nicht gemeldet: {stand.nichtDa.map((h) => `@${h}`).join(', ')}
        </p>
      )}

      <button
        onClick={zuruecksetzen}
        disabled={busy !== null}
        className="mt-auto py-2 text-sm text-text-muted underline decoration-dotted underline-offset-4"
      >
        Ziehung zurücksetzen
      </button>
    </main>
  )
}

function Rahmen({
  avatar,
  children,
}: {
  avatar: 'idle' | 'thinking' | 'speaking' | 'error'
  children: ReactNode
}) {
  return (
    <main className="flex min-h-[calc(100dvh_-_var(--buehne-leiste,0px))] flex-col items-center justify-center gap-6 bg-bg p-8 text-center">
      <Avatar state={avatar} size={160} showLabel={false} />
      <div className="flex w-full max-w-sm flex-col items-center gap-4">{children}</div>
    </main>
  )
}
